import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { 
  createBrowserRouter, 
  RouterProvider, 
  createRoutesFromElements,
  Route,
  Navigate
} from "react-router-dom";
import { useEffect } from "react";
import { useAuthStore } from "./lib/auth";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import Index from "./pages/Index";
import Registration from "./pages/Registration";
import Queue from "./pages/Queue";
import PatientView from "./pages/PatientView";

import History from "./pages/History";
import Settings from "./pages/Settings";
import Analytics from "./pages/Analytics";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Unauthorized from "./pages/Unauthorized";
import DoctorDashboard from "./pages/DoctorDashboard";
import EPrescription from "./pages/EPrescription";
import { ResetPassword } from "./pages/ResetPassword";
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SignUp } from '@/components/auth/SignUp';
import UserManagement from '@/pages/UserManagement';
import { selectiveRealtimeManager } from '@/lib/realtime';
import FollowUps from "@/pages/FollowUps";
import { AppWrapper } from '@/components/layout/AppWrapper';
import { useWallpaperStore } from '@/lib/stores/wallpaper';
import { useHospitalProfileStore } from '@/lib/stores/hospitalProfile';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => {
  const initialize = useAuthStore((state) => state.initialize);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);
  const loadWallpaper = useWallpaperStore((state) => state.loadWallpaper);
  const fetchProfile = useHospitalProfileStore((state) => state.fetchProfile);
  
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const setupAuth = async () => {
      cleanup = await initialize();
    };

    setupAuth();

    // Cleanup on unmount
    return () => {
      if (cleanup) cleanup();
      selectiveRealtimeManager.cleanup();
    };
  }, [initialize]);

  // Initialize realtime subscriptions only after authentication is complete
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      // Initialize realtime manager immediately - no delay needed
      selectiveRealtimeManager.initialize();
    }
  }, [isAuthenticated, isLoading]);

  // Initialize wallpaper when authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const initializeWallpaper = async () => {
        await fetchProfile();
        loadWallpaper();
      };
      
      initializeWallpaper();
    }
  }, [isAuthenticated, isLoading, fetchProfile, loadWallpaper]);

  // Add effect to handle logout cleanup
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up realtime subscriptions when user logs out
      selectiveRealtimeManager.cleanup();
    }
  }, [isAuthenticated]);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        {/* Public routes */}
        <Route path="/login" element={
          isLoading ? <div className="min-h-screen flex items-center justify-center">Loading...</div> :
          !isAuthenticated ? <Login /> : <Navigate to="/" replace />
        } />
        <Route path="/signup" element={
          !isAuthenticated ? <SignUp /> : <Navigate to="/" replace />
        } />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        
        {/* Doctor routes */}
        <Route path="/doctor-dashboard" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['doctor']}>
              <DoctorDashboard />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/e-prescription/:id" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['doctor']}>
              <EPrescription />
            </ProtectedRoute>
          </AppWrapper>
        } />
        
        {/* Protected routes */}
        <Route path="/" element={
          <AppWrapper>
            <ProtectedRoute>
              {user?.role === 'doctor' ? <Navigate to="/doctor-dashboard" replace /> : <Index />}
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/registration" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['staff']}>
              <Registration />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/queue" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Queue />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/patient/:id" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin', 'staff', 'doctor']}>
              <PatientView />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/followups" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['staff']}>
              <FollowUps />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/analytics" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin']}>
              <Analytics />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/history" element={
          <AppWrapper>
            <ProtectedRoute>
              <History />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/settings" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin']}>
              <Settings />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/users" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="*" element={<NotFound />} />
      </Route>
    ),
    {
      future: {
        v7_startTransition: true,
        v7_relativeSplatPath: true
      } as any
    }
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouterProvider router={router} />
          <Toaster />
          <Sonner />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
